// ADR 16.10.26k §2 — one tablist, where there were four copies.
//
// `ViewTabs`, `FlowTabs` and `CompanionSwitcher` each repeated the same
// roving `tabIndex` and key handler; this is that handler, once. Each item is
// a `Button variant="tab"`, so the selected tab is drawn by the
// `aria-selected` it already announces. ArrowRight / ArrowLeft wrap, Home and
// End go to the ends, ArrowDown / ArrowUp alias the horizontal arrows (the
// companion's keys, harmless on a row), and selection follows focus.
//
// While `disabled` every unselected tab is disabled and the selected one stays
// at `tabIndex=0`, so the tablist keeps its place in the tab order; the keys
// are still swallowed but move nothing (`ViewTabs`' rule, 15.10.26j §1).
//
// `ScriptTabs` closes a tab on Delete and draws a close glyph, so it keeps its
// own handler and borrows `TABLIST_CLASS` and the variant only.

import type { KeyboardEvent, ReactNode } from 'react'

import { Button } from '../Button'

/** `line` is the decorative-divider token (ADR 24.9.26k). */
export const TABLIST_CLASS = 'flex flex-wrap gap-1 border-b border-line'

export interface TabItem<T extends string> {
  value: T
  label: ReactNode
  id: string
  panelId: string
}

export interface TabsProps<T extends string> {
  /** The tablist's accessible name. */
  label: string
  items: readonly TabItem<T>[]
  selected: T
  disabled?: boolean
  onSelect(value: T): void
}

const STEP: Record<string, (at: number, last: number) => number> = {
  ArrowRight: (at, last) => (at === last ? 0 : at + 1),
  ArrowDown: (at, last) => (at === last ? 0 : at + 1),
  ArrowLeft: (at, last) => (at === 0 ? last : at - 1),
  ArrowUp: (at, last) => (at === 0 ? last : at - 1),
  Home: () => 0,
  End: (_, last) => last,
}

export function Tabs<T extends string>({ label, items, selected, disabled = false, onSelect }: TabsProps<T>) {
  const onKeyDown = (at: number, event: KeyboardEvent<HTMLButtonElement>) => {
    const step = STEP[event.key]
    if (step === undefined) return
    event.preventDefault()
    if (disabled) return
    const to = items[step(at, items.length - 1)]
    if (to.value !== selected) onSelect(to.value)
    document.getElementById(to.id)?.focus()
  }
  return (
    <div role="tablist" aria-label={label} className={TABLIST_CLASS}>
      {items.map((item, at) => (
        <Button
          key={item.value}
          id={item.id}
          variant="tab"
          role="tab"
          aria-selected={item.value === selected}
          aria-controls={item.panelId}
          tabIndex={item.value === selected ? 0 : -1}
          disabled={disabled && item.value !== selected}
          onClick={() => {
            if (item.value !== selected) onSelect(item.value)
          }}
          onKeyDown={(event) => onKeyDown(at, event)}
        >
          {item.label}
        </Button>
      ))}
    </div>
  )
}
